import React from 'react';
import { Grid, MediaMatch, Spinner, useViewport } from '@lamimed/pantheon';
import Head from 'next/head';
import { useRouter } from 'next/router';

import { useCategory } from 'hooks/useCategory';
import { SignInProvider } from 'hooks/useModalErrors';

import { defaultMetatags } from 'utils/fallbackValues/metatags';
import { sendToErrorTracking } from 'utils/sendToErrorTracking';

import { ContentSpoiler } from 'components/ContentSpoiler';
import { footerImageOffset } from 'components/Footer/styles';
import { Header } from 'components/Header';
import OurServices from 'components/OurServices';
import { SectionWrapper } from 'components/wrappers/SectionWrapper';

import { CategoryHeader } from './containers/CategoryHeader';
import { HowItWorks } from './containers/HowItWorks';
import { PrescriptionModal } from './containers/PrescriptionModal';
import { PrescriptionModalProvider } from './containers/PrescriptionModal/hooks/usePrescriptionModal';
import { Products } from './containers/Products';

const Category = () => {
  const router = useRouter();
  const { categoryData, categoryError, isCategoryLoading } = useCategory();
  const { greaterThan } = useViewport();

  if (categoryError) {
    sendToErrorTracking({
      error: categoryError,
      where: 'Category',
      context: categoryData,
    });

    router.push('/500');
  }

  return (
    <SignInProvider>
      <PrescriptionModalProvider>
        <Head>
          <title key="title">
            {categoryData?.metaTitle || defaultMetatags.title}
          </title>

          <meta
            name="description"
            content={
              categoryData?.metaDescription || defaultMetatags.description
            }
            key="description"
          />
        </Head>

        <Header />

        <SectionWrapper
          paddingTop="none"
          paddingBottom={greaterThan('tablet') ? 'xxlarge' : 'none'}
        >
          <MediaMatch greaterThan="tablet">
            <Grid.Wrapper>
              {categoryData && <CategoryHeader categoryData={categoryData} />}
            </Grid.Wrapper>
          </MediaMatch>

          <MediaMatch lessThan="tablet">
            {categoryData && <CategoryHeader categoryData={categoryData} />}
          </MediaMatch>
        </SectionWrapper>

        <SectionWrapper
          paddingBottom="none"
          paddingTop={greaterThan('tablet') ? 'none' : 'large'}
        >
          <Products haveContentBelow={!!categoryData?.additionalInfo} />
        </SectionWrapper>

        {isCategoryLoading && <Spinner />}

        {!!categoryData?.additionalInfo?.formattedContent && (
          <SectionWrapper paddingTop="none" paddingBottom="none">
            <Grid.Wrapper>
              <ContentSpoiler
                content={categoryData.additionalInfo.formattedContent}
                limit={300}
              />
            </Grid.Wrapper>
          </SectionWrapper>
        )}

        {categoryData && categoryData?.howItWorks?.length > 0 && (
          <SectionWrapper
            paddingTop="none"
            paddingBottom={greaterThan('tablet') ? 'xxlarge' : 'xlarge'}
          >
            <HowItWorks />
          </SectionWrapper>
        )}

        <SectionWrapper
          paddingTop="none"
          paddingBottom={greaterThan('tablet') ? 'xxlarge' : 'xlarge'}
          additionalPadding={footerImageOffset}
        >
          <Grid.Wrapper>
            <OurServices
              title="I nostri servizi"
              description="Scegli uno dei nostri servizi online o a domicilio e prenota in un click. Ti aiuteremo ovunque tu sia."
              image="/images/illustration/categories/homecare.png"
              color="gradient__secondary"
            />
          </Grid.Wrapper>
        </SectionWrapper>

        {categoryData?.slug === 'esami-test' && <PrescriptionModal />}
      </PrescriptionModalProvider>
    </SignInProvider>
  );
};

export default Category;
